<template>
  <div class="animated">
    <b-card>
      <div slot="header"><i class="icon-layers"></i> Sticky Templates</div>
      <b-button
        variant="success"
        v-on:click="openCreateModal"
        :disabled="!isPremium || data.length >= 25"
      >
        <font-awesome-icon icon="plus" style="margin-right: 5px" />Create new
        template
      </b-button>
      <br />
      <br />
      <p v-show="data.length >= 25 && isPremium" class="text-warning">
        Template limit reached (25/25)
      </p>
      <hr />
      <div v-if="loading">
        <div class="sk-circle-fade float-left">
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
        </div>
        <h2 class="float-left ml-4">Loading sticky templates...</h2>
      </div>
      <div
        v-else
        class="table-wrapper"
        :class="{ 'premium-disabled': !isPremium }"
      >
        <v-client-table
          :columns="columns"
          :data="data"
          :options="options"
          :theme="theme"
          id="dataTable"
          style="width: 100%; margin: none"
          aria-disabled="true"
        >
          <p
            slot="content"
            class="d-none d-md-inline-block"
            slot-scope="props"
            style="max-width: 780px !important"
            block
          >
            {{ props.row.content }}
            <b-badge
              v-if="Object.keys(JSON.parse(props.row.embed)).length > 1"
              class="ml-2"
              variant="primary"
              pill
              >embed</b-badge
            >
          </p>
          <b-button
            slot="actions"
            slot-scope="props"
            block
            variant="outline-primary"
            v-on:click="openEditModal(props.row)"
            >Edit</b-button
          >
        </v-client-table>
      </div>
    </b-card>

    <!-- Edit Modal -->
    <b-modal
      size="lg"
      v-model="editModal"
      @ok="editModal = false"
      :no-enforce-focus="true"
    >
      <template slot="modal-title">
        <h4 class="smalltitle">
          <i class="icon-layers" style="margin-right: 10px"></i>Edit Sticky
          Template
        </h4>
      </template>
      <b-form-group>
        <h4 class="smalltitle">Template Name</h4>
        <b-input-group>
          <b-form-input
            type="text"
            class="col-12"
            v-model="template_name"
            placeholder="Enter template name"
          ></b-form-input>
        </b-input-group>
        <hr />
        <h4 class="smalltitle">Content</h4>
        <b-input-group>
          <b-form-textarea
            rows="6"
            type="text"
            placeholder="Enter sticky message content"
            class="col-12"
            v-model="template_content"
          ></b-form-textarea>
        </b-input-group>
        <small>{{ template_content.length }}/2000</small>
        <hr />
        <EmbedBuilder ref="embed_builder_edit"></EmbedBuilder>
      </b-form-group>
      <div slot="modal-footer" class="w-100">
        <b-container
          fluid
          class="text-light text-center"
          style="padding-right: 0px"
        >
          <b-row class="mr-auto">
            <b-col cols="auto" class="mr-auto p-2">
              <b-button
                v-b-tooltip.hover
                title="Delete template"
                variant="ghost-danger"
                :disabled="template_id == undefined"
                v-on:click="deleteTemplate"
              >
                <i class="icon-trash"></i>
              </b-button>
            </b-col>
            <b-col cols="auto" class="p-2">
              <b-button variant="ghost-secondary" v-on:click="editModal = false"
                >Cancel</b-button
              >
            </b-col>
            <b-col cols="auto" class="p-2">
              <b-button variant="primary" v-on:click="editTemplate"
                >Save</b-button
              >
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-modal>

    <!-- Create Modal -->
    <b-modal
      size="lg"
      v-model="createModal"
      @ok="createModal = false"
      :no-enforce-focus="true"
    >
      <template slot="modal-title">
        <h4 class="smalltitle">
          <i class="icon-layers" style="margin-right: 10px"></i>Create Sticky
          Template
        </h4>
      </template>
      <b-form-group>
        <h4 class="smalltitle">Template Name</h4>
        <b-input-group>
          <b-form-input
            type="text"
            class="col-12"
            v-model="template_name"
            placeholder="Enter template name"
          ></b-form-input>
        </b-input-group>
        <hr />
        <h4 class="smalltitle">Content</h4>
        <b-input-group>
          <b-form-textarea
            rows="6"
            type="text"
            placeholder="Enter sticky message content"
            class="col-12"
            v-model="template_content"
          ></b-form-textarea>
        </b-input-group>
        <small>{{ template_content.length }}/2000</small>
        <hr />
        <EmbedBuilder ref="embed_builder_create"></EmbedBuilder>
      </b-form-group>
      <div slot="modal-footer" class="w-100">
        <b-container
          fluid
          class="text-light text-center"
          style="padding-right: 0px"
        >
          <b-row class="mr-auto">
            <b-col cols="auto" class="mr-auto p-2"></b-col>
            <b-col cols="auto" class="p-2">
              <b-button
                variant="ghost-secondary"
                v-on:click="createModal = false"
                >Cancel</b-button
              >
            </b-col>
            <b-col cols="auto" class="p-2">
              <b-button variant="success" v-on:click="createTemplate"
                >Create</b-button
              >
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import { Switch as cSwitch } from "@coreui/vue";
import { ClientTable, Event } from "vue-tables-2";
import VueNotifications from "vue-notifications";
import miniToastr from "mini-toastr";
import EmbedBuilder from "@/views/oc/EmbedBuilder";
import PatronCard from "@/views/oc/PatronCard";

const toastTypes = {
  success: "success",
  error: "error",
  info: "info",
  warn: "warn",
};
miniToastr.init({ types: toastTypes });

function toast({ title, message, type, timeout, cb }) {
  return miniToastr[type](message, title, timeout, cb);
}

const toast_options = {
  success: toast,
  error: toast,
  info: toast,
  warn: toast,
};
Vue.use(VueNotifications, toast_options);
Vue.use(ClientTable);

export default {
  name: "StickyTemplates",
  props: {
    isPremium: {
      type: Boolean,
      default: false,
    },
    patron: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ClientTable,
    Event,
    cSwitch,
    EmbedBuilder,
    PatronCard,
  },
  notifications: {
    added_template: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Sticky template created",
    },
    edited_template: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Sticky template edited",
    },
    deleted_template: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Sticky template deleted",
    },
    fill_in_fields: {
      type: VueNotifications.types.warn,
      title: "Uh oh",
      message: "Please fill in all fields",
    },
    create_error: {
      type: VueNotifications.types.error,
      title: "Uh oh",
      message: "Something went wrong!",
    },
  },
  data: function () {
    return {
      columns: ["name", "content", "actions"],
      data: [],
      options: {
        filterByColumn: true,
        columnsDisplay: {
          content: "not_mobile",
        },
        texts: {
          filter: "Filter",
          filterBy: "Filter by {column}",
        },
        sortable: ["name", "content"],
        filterable: ["name", "content"],
        sortIcon: {
          base: "fa",
          up: "fa-sort-asc",
          down: "fa-sort-desc",
        },
      },
      theme: "bootstrap4",
      editModal: false,
      createModal: false,
      template_name: "",
      template_content: "",
      template_id: null,
      loading: true,
    };
  },
  methods: {
    openEditModal(row) {
      this.template_name = row.name.trim();
      this.template_content = row.content.trim();
      this.template_id = row.id;
      this.editModal = true;
      var embed_var = JSON.parse(row.embed);
      setTimeout(() => {
        this.$refs.embed_builder_edit.apply_json(embed_var);
        this.$refs.embed_builder_edit.show_embed =
          Object.keys(embed_var).length > 1;
        this.$forceUpdate();
      }, 20);
    },
    openCreateModal() {
      this.template_name = "";
      this.template_content = "";
      this.createModal = true;
      setTimeout(() => {
        this.$refs.embed_builder_create.resetEmbed();
        this.$refs.embed_builder_create.show_embed = false;
      }, 20);
    },
    editTemplate() {
      if (
        !this.template_name.trim() ||
        (!this.template_content.trim() &&
          Object.keys(this.$refs.embed_builder_edit.full_json).length < 2)
      ) {
        return this.fill_in_fields();
      }

      this.data.forEach((e) => {
        if (e.id === this.template_id) {
          e.name = this.template_name;
          e.content = this.template_content;
          e.embed = JSON.stringify(this.$refs.embed_builder_edit.full_json);
        }
      });
      this.patchTemplate();
      this.edited_template();
    },
    createTemplate() {
      if (
        !this.template_name.trim() ||
        (!this.template_content.trim() &&
          Object.keys(this.$refs.embed_builder_create.full_json).length < 2)
      ) {
        return this.fill_in_fields();
      }

      if (this.template_name.trim().length < 3) {
        return this.create_error({
          message: "Template name cannot be less than 3 characters",
        });
      }

      this.putTemplate().then((res) => {
        if (res) {
          this.data.push({
            id: res,
            name: this.template_name,
            content: this.template_content,
            embed: JSON.stringify(this.$refs.embed_builder_create.full_json),
          });
        }
      });
    },
    patchTemplate() {
      this.axios
        .patch(
          "/api/v1/servers/" +
            this.$route.params.guild_id +
            "/sticky-templates",
          {
            id: this.template_id,
            name: this.template_name,
            content: this.template_content,
            embed: this.$refs.embed_builder_edit.full_json,
          }
        )
        .then(this.closeModals());
    },
    deleteTemplate() {
      this.axios
        .delete(
          "/api/v1/servers/" +
            this.$route.params.guild_id +
            "/sticky-templates/" +
            this.template_id
        )
        .then(this.closeModals());
      for (let [i, e] of this.data.entries()) {
        if (e.id === this.template_id) {
          this.data.splice(i, 1);
        }
      }
      this.deleted_template();
    },
    putTemplate() {
      this.closeModals();
      return this.axios
        .put(
          "/api/v1/servers/" +
            this.$route.params.guild_id +
            "/sticky-templates",
          {
            name: this.template_name,
            content: this.template_content,
            embed: this.$refs.embed_builder_create.full_json,
          }
        )
        .then((r) => {
          if (!r.data.success) {
            this.create_error({ message: r.data.msg });
            return;
          }
          setTimeout(
            () =>
              this.axios
                .get(
                  "/api/v1/servers/" +
                    this.$route.params.guild_id +
                    "/sticky-templates"
                )
                .then((r) => {
                  this.data = r.data.templates;
                }),
            2000
          );
          this.added_template();
          return r.data.id;
        });
    },
    closeModals() {
      this.editModal = false;
      this.createModal = false;
    },
    loadData() {
      if (this.isPremium === false) {
        this.loading = false;
        this.$Progress.finish();
        return;
      }

      this.axios
        .get(
          "/api/v1/servers/" + this.$route.params.guild_id + "/sticky-templates"
        )
        .then((r) => {
          this.data = r.data.templates;
          this.loading = false;
          this.$Progress.finish();
        })
        .catch((error) => {
          this.loading = false;
          this.$Progress.finish();
        });
    },
  },
  beforeRouteUpdate(to, from, next) {
    next();
    this.loadData();
  },
  created: function () {
    this.$Progress.start();
    this.loadData();
  },
};
</script>

<style lang="scss">
#dataTable {
  width: 95%;
  margin: 0 auto;

  .VuePagination {
    text-align: center;
    justify-content: center;
  }

  .td {
    word-wrap: break-word !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .vue-title {
    text-align: center;
    margin-bottom: 10px;
  }

  .VueTables__search-field {
    display: flex;
  }
  .VueTables__search-field input {
    margin-left: 0.25rem;
  }

  .VueTables__limit-field {
    display: flex;
  }

  .VueTables__limit-field select {
    margin-left: 0.25rem !important;
  }

  .VueTables__table th {
    text-align: center;
  }

  .VueTables__child-row-toggler {
    width: 16px;
    height: 16px;
    line-height: 16px;
    display: block;
    margin: auto;
    text-align: center;
  }

  .VueTables__child-row-toggler--closed::before {
    content: "+";
  }

  .VueTables__child-row-toggler--open::before {
    content: "-";
  }
}
// Add to your existing styles
.table-wrapper {
  position: relative;
}

.premium-disabled {
  .VueTables {
    opacity: 1;
    filter: blur(1px);
    pointer-events: none;
    user-select: none;
  }
}

.premium-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
}

.premium-message {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 2px solid #ffc107;

  h4 {
    color: #ffc107;
    margin-bottom: 1rem;
  }

  p {
    color: #6c757d;
    margin-bottom: 1.5rem;
  }
}
</style>
